<template>
    <div class="s-product-submenu">
        <div v-for="(item, i) in submenu" :key="`submenu-${i}`" class="item">
            <a-link
                v-if="item.to"
                class="link"
                v-bind="item"
                :title="item.text"
                :class="{ 'link-active': isActiveLink(item.to) }"
                :event="getDefaultALinkGAEvent(item)"
                :size="item.size || 'paragraph'"
                :type="theme === 'light' ? 'dark' : 'light'"
            />
            <div v-if="item.sublinks || item.submenu" ref="dropdown">
                <div
                    ref="sublist"
                    class="sublist-toggler"
                    :class="{ active: sublistOpened, dark: theme === 'light' }"
                    @click="toggleSublist"
                >
                    {{ item.text }}
                    <a-glyph class="sublist-glyph" name="drop-down" />
                </div>
                <div v-if="item.submenu" class="submenu">
                    <div class="columns">
                        <div v-for="(tab, j) in item.submenu.columns" :key="`${tab.text}-${j}`" class="column">
                            <div class="column-title">
                                {{ tab.text }}
                            </div>
                            <template v-for="(link, l) in tab.links">
                                <a-link
                                    v-if="link.to"
                                    :key="`${link.to}-${l}`"
                                    class="sublink"
                                    v-bind="link"
                                    :size="link.size || 'paragraph'"
                                    :class="{ 'link-active': isActiveLink(link.to) }"
                                    :event="getDefaultALinkGAEvent(link)"
                                />
                            </template>
                        </div>
                    </div>
                    <div v-if="item.submenu.bottomLinks" class="submenu-bottom">
                        <template v-for="(link, l) in item.submenu.bottomLinks">
                            <a-link
                                v-if="link.to"
                                :key="`${link.to}-${l}`"
                                class="link"
                                v-bind="link"
                                :size="link.size || 'paragraph'"
                                :event="getDefaultALinkGAEvent(link)"
                            />
                        </template>
                    </div>
                </div>
                <div v-else-if="item.sublinks" class="sublist">
                    <div class="sublist-inner">
                        <template v-for="(link, j) in item.sublinks">
                            <a-link
                                v-if="link.to"
                                :key="`sublink-${j}`"
                                class="sublink"
                                v-bind="link"
                                :size="link.size || 'paragraph'"
                                :class="{ 'link-active': isActiveLink(link.to) }"
                                :event="getDefaultALinkGAEvent(link)"
                            />
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AGlyph from '@core/components/glyph/glyph.vue';
import ALink from '@core/components/link/link.vue';

export default {
    name: 'SProductMenu',

    components: {
        AGlyph,
        ALink,
    },

    props: {
        submenu: {
            type: Array,
            required: true,
        },

        theme: {
            type: String,
            validator: (prop) => ['light', 'dark'].includes(prop),
            default: 'dark',
        },
    },

    data: () => ({
        sublistOpened: false,
    }),

    mounted() {
        document.addEventListener('click', this.hideDropdown);
    },

    methods: {
        getDefaultALinkGAEvent(item) {
            return {
                ...this.event,
                category: 'Interactions',
                action: 'top product tab',
                label: item.text,
            };
        },

        isActiveLink(link) {
            if (!this.$route) {
                // We're in Storybook, no refs available
                return false;
            }
            return this.$route.path === `/${this.locale}${link}`;
        },

        toggleSublist() {
            this.sublistOpened = !this.sublistOpened;
        },

        hideDropdown(event) {
            if (!this.sublistOpened || !this.$refs.dropdown) return;

            if (Array.isArray(this.$refs.dropdown)) {
                this.sublistOpened = this.$refs.dropdown.some((el) => el.contains(event.target));
            } else {
                this.sublistOpened = this.$refs.dropdown.contains(event.target);
            }
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-product-submenu {
    padding-inline-start: 48px;
    display: flex;
    flex-shrink: 0;
    white-space: nowrap;
    align-items: center;

    .item {
        &:not(:last-child) {
            margin-inline-end: 24px;
        }
    }

    .link {
        @mixin paragraph-accent;
        display: block;

        &.a-link_type_light {
            &:deep(.a-link__content) {
                color: var(--av-inversed-primary);
            }

            &:hover,
            &:focus {
                &:deep(.a-link__content) {
                    background: none;
                    color: var(--av-link-inversed);
                }
            }
        }

        &.link-active {
            font-weight: 600;
        }

        &::before {
            display: block;
            content: attr(title);
            font-weight: 600;
            height: 0;
            overflow: hidden;
            visibility: hidden;
        }
    }

    .sublist-container {
        position: relative;
        padding-bottom: 12px;
        margin-bottom: -12px;
    }

    .sublist-toggler {
        @mixin paragraph-accent;
        position: relative;
        display: flex;
        align-items: center;
        background: none;
        border: none;
        padding: 4px 8px;
        margin: -4px -8px;
        color: var(--av-inversed-primary);
        cursor: pointer;
        border-radius: 8px;

        & > * {
            pointer-events: none;
        }

        &:hover {
            color: var(--av-link-inversed);
        }

        &.dark {
            color: var(--av-link-secondary);

            &:hover {
                color: var(--av-link-secondary-hover);
            }
        }

        &.active {
            background: var(--av-brand-light);
            color: var(--av-inversed-primary);

            .sublist-glyph {
                transform: rotate(180deg);
                transition: transform 0.3s ease-out;
            }

            & + .sublist,
            & + .submenu  {
                display: block;
            }

            &::before {
                position: absolute;
                content: '';
                display: block;
                bottom: -20px;
                width: 0px;
                height: 0px;
                border-style: solid;
                border-width: 0 9px 7px 9px;
                border-color: transparent transparent rgb(64, 139, 234) transparent;
                inset-inline-end: 6px;
            }

            &::after {
                position: absolute;
                content: '';
                display: block;
                bottom: -21px;
                width: 0px;
                height: 0px;
                border-style: solid;
                border-width: 0 8px 6px 8px;
                border-color: transparent transparent #fff transparent;
                z-index: 1;
                inset-inline-end: 7px;
            }
        }
    }

    .sublist-glyph {
        margin-inline-start: 8px;
        fill: currentcolor;
        transition: transform 0.15s ease-out;
    }

    .sublist-inner,
    .submenu {
        background-color: var(--av-inversed-primary);
        box-shadow: 0 10px 20px rgba(36, 49, 67, 0.2);
        border-radius: 6px;
        border: 1px solid var(--av-brand-secondary);
    }

    .sublist {
        display: none;
        position: relative;
        margin-inline-start: 50%;
        top: 24px;
    }

    .sublist-inner {
        position: absolute;
        transform: translateX(-50%);
    }

    .sublist-inner {
        padding: 32px;
        inset-inline: auto;
        margin: auto;
        display: flex;
        flex-direction: column;
        width: fit-content;
    }

    .sublist-inner {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .submenu {
        display: none;
        position: absolute;
        width: 960px;
        top: 56px;
        inset-inline: 0;
        margin: auto;

        .columns {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 16px;
            padding: 32px;
        }

        .column {
            display: flex;
            flex-direction: column;
            gap: 8px;
            white-space: normal;
        }
    }

    .submenu-bottom {
        border-top: 1px solid var(--av-brand-light);
        background: var(--av-solid-brand-lightest);
        padding: 16px 32px;
        display: flex;
        justify-content: space-between;
        border-bottom-left-radius: 6px;
        border-bottom-right-radius: 6px;

        .link {
            &:deep(.a-link__content) {
                @mixin body-accent;
                color: var(--av-nav-primary);
            }

            &:deep(.a-glyph) {
                color: var(--av-brand-primary);
            }

            &:hover {
                &:deep(.a-link__content) {
                    color: var(--av-brand-secondary);
                }
            }
        }
    }

    .column-title {
        @mixin lead-accent;
        color: var(--av-nav-primary);
        margin-bottom: 8px;
    }

    .sublink {
        @mixin paragraph;
        position: relative;
        display: block;
        padding: 0;
        color: var(--av-brand-primary);
        cursor: pointer;

        &:before {
            content: '';
            display: block;
            position: absolute;
            inset: -4px -8px;
            border-radius: 4px;
        }

        &:hover,
        &.link-hover {
            &:before {
                background: var(--av-brand-secondary-accent);
            }
        }

        &:active {
            &:before {
                background: var(--av-brand-secondary-light);
            }
        }
    }
}
</style>
